import React, { lazy, Suspense, ComponentType } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom'
import { Behavior, Indicators } from 'ui'
import { usePageViews } from './usePageViews'
import { SessionRedirect } from './SessionRedirect'
import { RequireAuth } from './RequireAuth'
import { RequireAdmin } from './RequireAdmin'
import { RedirectIfAuthenticated } from './RedirectIfAuthenticated'
import { RequireOnboarding } from './RequireOnboarding'
import { RequireApproval } from './RequireApproval'

/**
 * Helper function to create a lazy-loaded component from a named export
 * @param factory Import function for the module
 * @param exportName Name of the exported component to use as default
 */
function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ [key: string]: T }>,
  exportName: string
) {
  return lazy(() =>
    factory()
      .then((module) => {
        if (!module || !module[exportName]) {
          console.error(
            `Module loaded but ${exportName} not found in:`,
            Object.keys(module)
          )
          throw new Error(`Component ${exportName} not found in loaded module`)
        }
        return { default: module[exportName] }
      })
      .catch((error) => {
        console.error(`Error lazy loading component ${exportName}:`, error)
        return {
          default: () => (
            <div
              style={{ padding: 15, margin: 10, border: '1px solid #f44336' }}
            >
              <h4>Component Loading Error: {exportName}</h4>
              <p>Failed to load this component.</p>
              <details>
                <summary>Error details</summary>
                <pre>{String(error)}</pre>
              </details>
            </div>
          ),
        }
      })
  )
}

// Base views that are critical for initial load
import {
  ForgotPasswordView,
  InitialSignUpView,
  LoginView,
  ResetPasswordView,
  TokenView,
  SessionExpiredView,
} from '../views'

/**
 * Lazy-loaded components for improved code splitting
 */

// Create safer lazy loader with error handling
const safeLazy = (importFn) => {
  return lazy(() =>
    importFn().catch((error) => {
      console.error(`Error lazy loading component:`, error)
      return {
        default: () => (
          <div style={{ padding: 15, margin: 10, border: '1px solid #f44336' }}>
            <h4>Component Loading Error</h4>
            <p>Failed to load this component.</p>
            <details>
              <summary>Error details</summary>
              <pre>{String(error)}</pre>
            </details>
          </div>
        ),
      }
    })
  )
}

// Admin components (already use default exports via barrel files)
const AdminView = safeLazy(() => import('./AdminView'))
const AppNavigator = safeLazy(() => import('./AppNavigator'))

// Admin components
const AdminDashboardView = lazyWithPreload(
  () => import('../views/Admin/AdminDashboardView'),
  'AdminDashboardView'
)
const AdminRolesView = lazyWithPreload(
  () => import('../views/Admin/AdminRolesView'),
  'AdminRolesView'
)
const EventsView = lazyWithPreload(
  () => import('../views/Admin/EventsView'),
  'EventsView'
)
const InvitationsView = lazyWithPreload(
  () => import('../views/Admin/InvitationsView'),
  'InvitationsView'
)
const ResourcesView = lazyWithPreload(
  () => import('../views/Admin/ResourcesView'),
  'ResourcesView'
)
const ReportsView = lazyWithPreload(
  () => import('../views/Admin/ReportsView'),
  'ReportsView'
)
const UserProfilesView = lazyWithPreload(
  () => import('../views/Admin/UserProfilesView'),
  'UserProfilesView'
)
const DataManagementView = lazyWithPreload(
  () => import('../views/Admin/DataManagement'),
  'DataManagementView'
)
const SchoolsView = lazyWithPreload(
  () => import('../views/Admin/SchoolsView'),
  'SchoolsView'
)

// App Navigator components
const DashboardView = lazyWithPreload(
  () => import('../views/AppNavigator/Dashboard'),
  'DashboardView'
)
const DirectoryListView = lazyWithPreload(
  () => import('../views/AppNavigator/DirectoryView/DirectoryListView'),
  'DirectoryListView'
)
const FavoritesIndexView = lazyWithPreload(
  () => import('../views/AppNavigator/FavoritesView'),
  'FavoritesIndexView'
)

// Match related components
const MatchesListView = lazyWithPreload(
  () => import('../views/AppNavigator/Matches/MatchesListView'),
  'MatchesListView'
)
const MatchDetailView = lazyWithPreload(
  () => import('../views/AppNavigator/Matches/MatchDetailView'),
  'MatchDetailView'
)

// Message related components
const MessagesListView = lazyWithPreload(
  () => import('../views/AppNavigator/Messages/MessagesListView'),
  'MessagesListView'
)
const MessageDetailView = lazyWithPreload(
  () => import('../views/AppNavigator/Messages/MessageDetailView'),
  'MessageDetailView'
)

// Settings components
const SettingsIndexView = lazyWithPreload(
  () => import('../views/AppNavigator/Settings/SettingsIndexView'),
  'SettingsIndexView'
)

// Resource components
const ResourcesIndexView = lazyWithPreload(
  () => import('../views/AppNavigator/Resources/ResourcesIndexView'),
  'ResourcesIndexView'
)
const ResourcesListView = lazyWithPreload(
  () => import('../views/AppNavigator/Resources/ResourcesListView'),
  'ResourcesListView'
)
const ResourceDetailView = lazyWithPreload(
  () => import('../views/AppNavigator/Resources/ResourceDetailView'),
  'ResourceDetailView'
)
const SavedSearchesIndexView = lazyWithPreload(
  () =>
    import('../views/AppNavigator/SavedSearchesView/SavedSearchesIndexView'),
  'SavedSearchesIndexView'
)

// Signup components
const CreateAccountLayout = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/CreateAccountView'),
  'CreateAccountLayout'
)
const OnboardView = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/OnboardView'),
  'OnboardView'
)
const EducatorStep = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/EducatorStep'),
  'EducatorStep'
)
const EducatorStepOne = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/EducatorStepOne'),
  'EducatorStepOne'
)
const EducatorStepTwo = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/EducatorStepTwo'),
  'EducatorStepTwo'
)
const EducatorStepThree = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/EducatorStepThree'),
  'EducatorStepThree'
)
const EducatorStepFour = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/EducatorStepFour'),
  'EducatorStepFour'
)
const ProfessionalStep = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ProfessionalStep'),
  'ProfessionalStep'
)
const ProfessionalStepOne = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ProfessionalStepOne'),
  'ProfessionalStepOne'
)
const ProfessionalStepTwo = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ProfessionalStepTwo'),
  'ProfessionalStepTwo'
)
const ProfessionalStepThree = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ProfessionalStepThree'),
  'ProfessionalStepThree'
)
const ProfessionalStepFour = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ProfessionalStepFour'),
  'ProfessionalStepFour'
)
const ConfirmationView = lazyWithPreload(
  () => import('../views/SignUp/CreateAccount/ConfirmationView'),
  'ConfirmationView'
)

// Event components
const EventsIndexView = lazyWithPreload(
  () => import('../views/AppNavigator/Events/EventsIndexView'),
  'EventsIndexView'
)
const Logout = lazyWithPreload(
  () => import('../views/AppNavigator/Logout'),
  'Logout'
)

const DefaultRoutes: React.FC = () => {
  console.log('RootRouter.tsx: Rendering DefaultRoutes component')

  // Safe hook usage - wrapped in error handling
  try {
    usePageViews()
    console.log('RootRouter.tsx: usePageViews hook called')
  } catch (hookError) {
    console.error('DefaultRoutes: Error in usePageViews hook:', hookError)
    // Continue execution - this hook is not critical
  }

  try {
    console.log('DefaultRoutes: Creating Routes component')

    // Create a safer Routes component with basic routes first
    return (
      <Routes>
        {/* Public Routes - with individual error boundaries */}
        <Route
          path="/"
          element={
            <RouteErrorBoundary route="/">
              <RedirectIfAuthenticated>
                <InitialSignUpView />
              </RedirectIfAuthenticated>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/session"
          element={
            <RouteErrorBoundary route="/session">
              <SessionRedirect />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/session-expired"
          element={
            <RouteErrorBoundary route="/session-expired">
              <SessionExpiredView />
            </RouteErrorBoundary>
          }
        />

        {/* Signup Routes */}
        <Route
          path="/signup/confirm"
          element={
            <RedirectIfAuthenticated>
              <ConfirmationView />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/signup"
          element={
            <RedirectIfAuthenticated>
              <CreateAccountLayout />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/signup/educator"
          element={
            <RedirectIfAuthenticated>
              <CreateAccountLayout />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/signup/professional"
          element={
            <RedirectIfAuthenticated>
              <CreateAccountLayout />
            </RedirectIfAuthenticated>
          }
        />

        {/* Onboarding Routes */}
        <Route path="/onboard/*" element={<OnboardView />}>
          <Route path="educator/start" element={<EducatorStep />} />
          <Route path="educator/one" element={<EducatorStepOne />} />
          <Route path="educator/two" element={<EducatorStepTwo />} />
          <Route path="educator/three" element={<EducatorStepThree />} />
          <Route path="educator/four" element={<EducatorStepFour />} />
          <Route path="professional/start" element={<ProfessionalStep />} />
          <Route path="professional/one" element={<ProfessionalStepOne />} />
          <Route path="professional/two" element={<ProfessionalStepTwo />} />
          <Route
            path="professional/three"
            element={<ProfessionalStepThree />}
          />
          <Route path="professional/four" element={<ProfessionalStepFour />} />
        </Route>

        {/* Login Routes */}
        <Route
          path="/login/forgot"
          element={
            <RedirectIfAuthenticated>
              <ForgotPasswordView />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/login/reset"
          element={
            <RedirectIfAuthenticated>
              <ResetPasswordView />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <LoginView />
            </RedirectIfAuthenticated>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <RequireAdmin>
              <Suspense fallback={<Indicators.FullScreen />}>
                <AdminView />
              </Suspense>
            </RequireAdmin>
          }
        >
          <Route path="dashboard" element={<AdminDashboardView />} />
          <Route path="events/*" element={<EventsView />} />
          <Route path="invitations" element={<InvitationsView />} />
          <Route path="logout" element={<Logout />} />
          <Route path="profiles/*" element={<UserProfilesView />} />
          <Route path="resources/*" element={<ResourcesView />} />
          <Route path="roles/*" element={<AdminRolesView />} />
          <Route path="schools/*" element={<SchoolsView />} />
          <Route path="management" element={<DataManagementView />} />
          <Route path="reports" element={<ReportsView />} />
          <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        {/* App Routes */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="" element={<Navigate to="/app/dashboard" replace />} />
        </Route>

        {/* Dashboard Route */}
        <Route
          path="/app/dashboard/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="" element={<DashboardView />} />
        </Route>

        {/* Directory Routes */}
        <Route
          path="/app/directory/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="" element={<DirectoryListView />} />
        </Route>

        {/* Events Routes */}
        <Route
          path="/app/events/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="*" element={<EventsIndexView />} />
        </Route>

        {/* Favorites Routes */}
        <Route
          path="/app/favorites/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="" element={<FavoritesIndexView />} />
        </Route>

        {/* Logout Route - RequireApproval intentionally omitted to allow unapproved users to logout */}
        <Route
          path="/app/logout/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <Suspense fallback={<Indicators.FullScreen />}>
                  <Logout />
                </Suspense>
              </RequireOnboarding>
            </RequireAuth>
          }
        />

        {/* Matches Routes */}
        <Route
          path="/app/matches/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path=":profileId" element={<MatchDetailView />} />
          <Route path="" element={<MatchesListView />} />
        </Route>

        {/* Messages Routes */}
        <Route
          path="/app/messages/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path=":threadId" element={<MessageDetailView />} />
          <Route path="" element={<MessagesListView />} />
        </Route>

        {/* Saved Searches Routes */}
        <Route
          path="/app/searches/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="" element={<SavedSearchesIndexView />} />
        </Route>

        {/* Resources Routes */}
        <Route
          path="/app/resources/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <RequireApproval>
                  <Suspense fallback={<Indicators.FullScreen />}>
                    <AppNavigator />
                  </Suspense>
                </RequireApproval>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="*" element={<ResourcesIndexView />} />
        </Route>

        {/* Settings Routes - RequireApproval intentionally omitted to allow users with incomplete profiles to edit their profile */}
        <Route
          path="/app/settings/*"
          element={
            <RequireAuth>
              <RequireOnboarding>
                <Suspense fallback={<Indicators.FullScreen />}>
                  <AppNavigator />
                </Suspense>
              </RequireOnboarding>
            </RequireAuth>
          }
        >
          <Route path="*" element={<SettingsIndexView />} />
        </Route>

        {/* Token Route */}
        <Route path="/token/:token" element={<TokenView />} />

        {/* Fallback Routes */}
        <Route
          path="/admin/*"
          element={<Navigate to="/admin/dashboard" replace />}
        />
        <Route
          path="/app/*"
          element={<Navigate to="/app/dashboard" replace />}
        />
      </Routes>
    )
  } catch (error) {
    console.error('DefaultRoutes: Error rendering routes:', error)
    return (
      <div style={{ padding: 20 }}>
        <h2>Error loading routes</h2>
        <p>There was an error loading the application routes.</p>
        <details>
          <summary>Error details</summary>
          <pre>{String(error)}</pre>
        </details>
      </div>
    )
  }
}

export const RootRouter: React.FunctionComponent = () => {
  console.log('RootRouter.tsx: Rendering RootRouter component')

  // Use a state to track router errors
  const [routerError, setRouterError] = React.useState<Error | null>(null)

  // Setup a safer router wrapped in its own try/catch
  const renderSafeRouter = () => {
    try {
      // Create a safer scroll handler
      const renderScrollToTop = () => {
        try {
          return <Behavior.ScrollToTop />
        } catch (scrollError) {
          console.error('RootRouter: Error with ScrollToTop:', scrollError)
          return null // Skip the ScrollToTop if it's causing problems
        }
      }

      return (
        <Router>
          <ErrorBoundary component="ScrollToTop">
            {renderScrollToTop()}
          </ErrorBoundary>
          <Helmet>
            <meta charSet="utf-8" />
            <title>MCSS PRO Connect</title>
          </Helmet>
          <ErrorBoundary component="DrawerManager">
            <ErrorBoundary>
              <Suspense fallback={<Indicators.FullScreen />}>
                <DefaultRoutes />
              </Suspense>
            </ErrorBoundary>
          </ErrorBoundary>
        </Router>
      )
    } catch (error) {
      console.error('RootRouter.tsx: Router instantiation error:', error)
      setRouterError(error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  // Handle router errors with a fallback UI
  if (routerError) {
    return (
      <div
        style={{
          padding: 20,
          maxWidth: 800,
          margin: '40px auto',
          border: '1px solid #ccc',
          borderRadius: 8,
        }}
      >
        <h2 style={{ color: '#e53935' }}>Router Error</h2>
        <p>There was an error initializing the application router.</p>
        <p>
          This is often caused by invalid route configurations or component
          issues.
        </p>
        <details style={{ marginTop: 15 }}>
          <summary style={{ cursor: 'pointer' }}>Error details</summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              padding: 10,
              backgroundColor: '#f5f5f5',
              marginTop: 10,
            }}
          >
            {routerError.message}
            {routerError.stack && `\n\n${routerError.stack}`}
          </pre>
        </details>
      </div>
    )
  }

  // Render the application router
  return renderSafeRouter()
}

// Error boundary component for RootRouter
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    console.error('RootRouter ErrorBoundary caught error:', error)
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('RootRouter ErrorBoundary details:', { error, errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'none' }}>
          <h2>Router Error</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </details>
        </div>
      )
    }

    return this.props.children
  }
}

// Error boundary for individual routes
class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode; route: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; route: string }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    // Log a more consistent message (avoids capturing 'this' which is undefined)
    console.error('Route Error in error boundary:', error)
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Route Error Details (${this.props.route}):`, {
      error,
      errorInfo,
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20 }}>
          <h3>Error in route: {this.props.route}</h3>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </details>
        </div>
      )
    }

    return this.props.children
  }
}
